import type {
  CustomSEO,
  CustomSEOOnPage,
  SEOPageType,
} from '@iheartjane/data-access';
import { SEO_PAGE_TYPES } from '@iheartjane/data-access';
import {
  Banner,
  AlertIcon,
  Flex,
  Tabs,
  useToast,
  Box,
} from '@iheartjane/reefer';
import isEmpty from 'lodash-es/isEmpty';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';

import {
  useFetchCurrentBloomMenu,
  useUpdateBloomMenu,
} from '@jane/business-admin/data-access';
import type { BloomOperatorConfig } from '@jane/business-admin/types';
import { EventNames, businessPaths, track } from '@jane/business-admin/util';

import { ModalPageWrapper } from './components/ModalPageWrapper';
import { SeoFormProvider } from './providers/SeoFormProvider';
import { NAV_LINKS } from './seoTabs/defaultSeoConstants';
import { PageSeo } from './seoTabs/PageSeo';
import { CATEGORIES_ORDER } from './utils/categories';

import styles from './PremiumSeo.module.css';

export const PremiumSeo = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [activeSection, setActiveSection] = useState<SEOPageType>('featured');
  const [isLoading, setIsLoading] = useState(false);

  const bloomMenuQuery = useFetchCurrentBloomMenu();
  const { mutate: updateBloomMenu } = useUpdateBloomMenu(
    bloomMenuQuery.data?.id
  );

  const resolvedSeo = bloomMenuQuery.data?.custom_seo;
  const faviconUrl = bloomMenuQuery.data?.favicon_url;
  const seoDefaults = bloomMenuQuery.data?.custom_seo_defaults;

  const initialValues = useMemo<CustomSEO>(() => {
    const initialValues: CustomSEO = {};
    SEO_PAGE_TYPES.forEach((seoType) => {
      initialValues[seoType] = {
        title: resolvedSeo?.[seoType]?.title ?? '',
        description: resolvedSeo?.[seoType]?.description ?? '',
      };
    });
    const onPage: CustomSEOOnPage = {};
    CATEGORIES_ORDER.forEach((kind) => {
      onPage[kind] = {
        title: resolvedSeo?.onPage?.[kind]?.title ?? '',
        description: resolvedSeo?.onPage?.[kind]?.description ?? '',
      };
    });
    initialValues.onPage = onPage;
    return initialValues;
  }, [resolvedSeo]);

  const formMethods = useForm<CustomSEO>({
    defaultValues: initialValues,
    reValidateMode: 'onChange',
    mode: 'all',
  });
  const {
    formState: { errors },
    reset,
    trigger,
  } = formMethods;

  const seoVariables = useMemo(() => {
    return {
      storeName: bloomMenuQuery.data?.stores[0]?.name,
      storeCity: bloomMenuQuery.data?.stores[0]?.city,
      storeStateAbbr: bloomMenuQuery.data?.stores[0]?.state,
      storeType: 'Pickup',
      storeFulfillment: bloomMenuQuery.data?.stores[0]?.recreational
        ? 'Recreational'
        : 'Medical',
      category: 'flower',
      menuUrl: bloomMenuQuery.data?.menu_url || '',
      specialName: 'Bogo great deal',
      rowName: 'row 1',
      productName: 'Green Chocolate',
      productLineage: 'Sativa',
      productCategory: 'Edible',
      productSubCategory: 'Chocolates',
      productBrand: "Jane's Bakery",
    };
  }, [bloomMenuQuery.data]);

  useEffect(() => {
    if (bloomMenuQuery.isSuccess && resolvedSeo) {
      reset(initialValues);
      trigger();
    }
  }, [bloomMenuQuery.isSuccess, resolvedSeo, initialValues, reset, trigger]);

  const publishSeoCustomization = (data: CustomSEO) => {
    setIsLoading(true);
    updateBloomMenu(
      {
        custom_seo: data,
      },
      {
        onError: () => {
          toast.add({
            label: 'An error occurred.',
            variant: 'error',
          });
          setIsLoading(false);
        },
        onSuccess: async () => {
          toast.add({
            label: 'SEO customization updated!',
            variant: 'success',
          });

          const bloomMenuData = bloomMenuQuery.data as BloomOperatorConfig;
          track({
            event: EventNames.EditedSeoInformation,
            operator_id: bloomMenuData.operator.id,
            operator_name: bloomMenuData.operator.name,
            old_content: bloomMenuData.custom_seo,
            new_content: data,
          });

          setIsLoading(false);
          navigate(businessPaths.premium());
        },
      }
    );
  };

  const scrollToSection = (pageType: SEOPageType) => {
    setActiveSection(pageType);
    document
      .getElementById(pageType)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const errorMessages = useMemo(() => {
    if (isEmpty(errors)) return undefined;

    const messages: string[] = [];

    const sectionNames = Object.keys(errors)
      .map((key) => {
        return NAV_LINKS.find((link) => link.value === key)?.label;
      })
      .filter(Boolean);
    if (errors['onPage'] && !sectionNames.includes('Category')) {
      sectionNames.push('Category');
    }
    if (sectionNames.length > 0) {
      messages.push(
        `Please fix the errors in ${sectionNames.join(', ')} to publish.`
      );
    }
    return messages;
  }, [JSON.stringify(errors)]);

  return (
    <ModalPageWrapper
      formMethods={formMethods}
      isLoading={isLoading}
      modalOverflow="hidden"
      onSubmit={publishSeoCustomization}
      title="SEO Customization"
    >
      {/* 120 = 80px (header) + 40px (padding) */}
      <Flex
        height="calc(100% - 120px)"
        gap={40}
        mt={40}
        justifyContent="center"
      >
        <nav
          aria-label="SEO customization navigation"
          className={styles['nav']}
        >
          <Tabs<SEOPageType>
            direction="vertical"
            onChange={scrollToSection}
            tabWidth="200px"
            value={activeSection}
          >
            {NAV_LINKS.map((item) => (
              <Tabs.Tab
                value={item.value}
                key={item.value}
                label={
                  errors[item.value] ||
                  (errors['onPage'] && item.value === 'category') ? (
                    <Flex alignItems="center">
                      <AlertIcon mr={8} size="sm" color="error" />
                      {item.label}
                    </Flex>
                  ) : (
                    item.label
                  )
                }
              />
            ))}
          </Tabs>
        </nav>

        <Box
          className={styles['sections']}
          minHeight="0"
          overflowY="auto"
          pb={40}
          maxWidth="944px"
        >
          <SeoFormProvider>
            <Flex flexDirection="column" gap={24}>
              {errorMessages &&
                errorMessages.length > 0 &&
                errorMessages.map((errorMessage, i) => (
                  <Banner
                    key={`error-${i}`}
                    variant="error"
                    label={errorMessage}
                    icon={<AlertIcon />}
                  />
                ))}

              <PageSeo
                menuUrl={`${seoVariables['menuUrl']}`}
                pageType="featured"
                defaults={seoDefaults?.featured}
                faviconUrl={faviconUrl}
                pageDescription="the homepage"
                seoVariables={seoVariables}
              />
              <PageSeo
                menuUrl={`${seoVariables['menuUrl']}/${seoVariables['category']}`}
                pageType="category"
                defaults={seoDefaults?.category}
                faviconUrl={faviconUrl}
                pageDescription="category pages"
                seoVariables={seoVariables}
              />
              <PageSeo
                menuUrl={`${seoVariables['menuUrl']}/deals/489/${
                  seoVariables['specialName']
                }`}
                pageType="deal"
                defaults={seoDefaults?.deal}
                faviconUrl={faviconUrl}
                pageDescription="deal pages"
                seoVariables={seoVariables}
              />
              <PageSeo
                menuUrl={`${seoVariables['menuUrl']}/shop-all`}
                pageType="shopAll"
                defaults={seoDefaults?.shopAll}
                faviconUrl={faviconUrl}
                pageDescription="shop all pages"
                seoVariables={{
                  ...seoVariables,
                  category: 'shop all',
                }}
              />
              <PageSeo
                menuUrl={`${seoVariables['menuUrl']}/customRow/81917/${
                  seoVariables['rowName']
                }`}
                pageType="customRow"
                defaults={seoDefaults?.customRow}
                faviconUrl={faviconUrl}
                pageDescription="custom row pages"
                seoVariables={seoVariables}
              />
              <PageSeo
                menuUrl={`${seoVariables['menuUrl']}/products/147331/foria-sativa-hawaiian-haze`}
                pageType="pdp"
                defaults={seoDefaults?.pdp}
                faviconUrl={faviconUrl}
                pageDescription="pdp pages"
                seoVariables={seoVariables}
              />
            </Flex>
          </SeoFormProvider>
        </Box>
      </Flex>
    </ModalPageWrapper>
  );
};
